// components/FaqSectionClient.tsx  (CLIENT) ✅ tylko to co musi być client
'use client';

import { useEffect, useState } from 'react';
import ButtonLandingPage from '@/components/landing/ButtonLandingPage';
import CtaButton from '@/components/ui/CtaButton';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@relume_io/relume-ui';
import { LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion';

export type FaqItemVM = {
    _key: string;
    title: string;
    answer: string;
};

type Props = {
    heading: string;
    description: string;
    faqs: FaqItemVM[];
    useLandingButton?: boolean;
};

const slideUp = {
    hidden: { y: 40, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

function PlusIcon({ className }: { className?: string }) {
    // zamiast react-icons (mniej JS), wygląd = ten sam “plus”
    return (
        <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className={className}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
        </svg>
    );
}

function FaqAccordionList({
    faqs,
    prefersReduced,
}: {
    faqs: FaqItemVM[];
    prefersReduced: boolean | null;
}) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) {
        return (
            <div className="grid items-start justify-stretch gap-4" aria-hidden="true">
                {faqs.map((item) => (
                    <div
                        key={item._key}
                        className="rounded-2xl bg-primary-light border-none px-5 py-5 md:px-6 md:py-5"
                    >
                        <p className="font-medium text-left md:text-md">{item.title}</p>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <Accordion type="multiple" className="grid items-start justify-stretch gap-4">
            {faqs.map((item, idx) => (
                <m.div
                    key={item._key}
                    variants={prefersReduced ? undefined : slideUp}
                    custom={idx + 2}
                >
                    <AccordionItem
                        value={item._key}
                        className="rounded-2xl bg-primary-light border-none px-5 md:px-6"
                    >
                        <AccordionTrigger
                            icon={
                                <PlusIcon className="size-7 shrink-0 text-black transition-transform duration-300 md:size-8" />
                            }
                            className="!font-medium md:py-5 md:text-md [&[data-state=open]>svg]:rotate-45"
                        >
                            <span className="!font-medium text-left">{item.title}</span>
                        </AccordionTrigger>

                        <AccordionContent className="md:pb-6">{item.answer}</AccordionContent>
                    </AccordionItem>
                </m.div>
            ))}
        </Accordion>
    );
}

export default function FaqSectionClient({ heading, description, faqs, useLandingButton = false }: Props) {
    const prefersReduced = useReducedMotion();

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="px-[5%] py-16 md:py-24 lg:py-28 overflow-hidden"
            >
                <div className="container grid grid-cols-1 gap-y-12 md:grid-cols-2 md:gap-x-12 lg:grid-cols-[.75fr,1fr] lg:gap-x-20">
                    {/* Lewa kolumna */}
                    <div>
                        {heading && (
                            <m.h2
                                variants={slideUp}
                                custom={0}
                                className="mb-5 text-xl font-medium md:mb-6 md:text-3xl"
                            >
                                {heading}
                            </m.h2>
                        )}

                        {description && (
                            <m.p variants={slideUp} custom={1} className="md:text-md">
                                {description}
                            </m.p>
                        )}

                        <m.div variants={slideUp} custom={2} className="mt-6 md:mt-8">
                            {useLandingButton ? <ButtonLandingPage /> : <CtaButton />}
                        </m.div>
                    </div>

                    <FaqAccordionList faqs={faqs} prefersReduced={prefersReduced} />
                </div>
            </m.section>
        </LazyMotion>
    );
}